import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { MultiselectSearchOption, MultiSelectSearchableMode } from '@shared/ui/fields'

import { useListsStore } from '@shared/model'

import { useTutorKanbanStore } from './store'
import { useTutorKanbanExitConfirm } from './tutor-kanban-exit-confirm.hook'
import { getTutorStageReasonKey } from '../config/tutor-kanban-stage-reason-key.const'

interface TutorKanbanSelectReasonReturn {
  isVisible: ComputedRef<boolean>
  refOptions: ComputedRef<MultiselectSearchOption[]>
  refValue: ComputedRef<string[]>
  refMode: ComputedRef<MultiSelectSearchableMode>
  refResetKey: ComputedRef<number>
  isConfirmOpen: ComputedRef<boolean>
  hasPendingChange: ComputedRef<boolean>
  setSelected: (options: MultiselectSearchOption[]) => void
  setDropdownClose: () => void
  setConfirmRequest: () => void
  setConfirmChange: () => void
  setCancelChange: () => void
}

const isSameSelection = (values: string[], current: string[]): boolean => {
  return values.length === current.length && values.every(value => current.includes(value))
}

export const useTutorKanbanSelectReason = (): TutorKanbanSelectReasonReturn => {
  const tutorKanbanStore = useTutorKanbanStore()
  const listsStore = useListsStore()
  const { isReasonConfirmRequired } = useTutorKanbanExitConfirm()

  const refStageReasons = computed<Record<string, string>>(() => {
    const key = getTutorStageReasonKey(tutorKanbanStore.getStage)
    const list = listsStore.getLists?.tutor_stage_reason
    if (!key || !list) return {}
    return list[key] ?? {}
  })

  const isVisible = computed<boolean>(() => Object.keys(refStageReasons.value).length > 0)

  const refValue = computed<string[]>(() => tutorKanbanStore.getTutor?.stage_details?.reasons ?? [])

  const refResetKey = computed<number>(() => tutorKanbanStore.getReasonResetKey)

  const isConfirmOpen = computed<boolean>(() => tutorKanbanStore.getReasonConfirmSource !== null)

  const hasPendingChange = computed<boolean>(() => {
    const values = tutorKanbanStore.getPendingStageReasons
    return !!values && !isSameSelection(values, refValue.value)
  })

  const refOptions = computed<MultiselectSearchOption[]>(() => {
    const selected = refValue.value
    return Object.entries(refStageReasons.value).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: selected.includes(value),
    }))
  })

  const refMode = computed<MultiSelectSearchableMode>(() => {
    if (tutorKanbanStore.isValid) return 'normal'
    if (isVisible.value && refValue.value.length === 0) return 'error'
    return 'normal'
  })

  const setReasons = (values: string[]): void => {
    tutorKanbanStore.setTutorStageReasons(values)
  }

  const setSelected = (options: MultiselectSearchOption[]): void => {
    const values = options.map(option => String(option.value))

    if (isReasonConfirmRequired.value) {
      tutorKanbanStore.setPendingStageReasons(values)
      return
    }

    setReasons(values)
  }

  const setDropdownClose = (): void => {
    if (!tutorKanbanStore.getPendingStageReasons) return

    if (!hasPendingChange.value) {
      tutorKanbanStore.setPendingStageReasons(null)
      return
    }

    tutorKanbanStore.setReasonConfirmSource('field')
  }

  const setConfirmRequest = (): void => {
    tutorKanbanStore.setReasonConfirmSource('save')
  }

  const setConfirmChange = (): void => {
    setReasons(tutorKanbanStore.getPendingStageReasons ?? [])
    tutorKanbanStore.setPendingStageReasons(null)
    tutorKanbanStore.setReasonConfirmSource(null)
  }

  const setCancelChange = (): void => {
    tutorKanbanStore.setPendingStageReasons(null)
    tutorKanbanStore.setReasonConfirmSource(null)
    tutorKanbanStore.setReasonResetKey(tutorKanbanStore.getReasonResetKey + 1)
  }

  return {
    isVisible,
    refOptions,
    refValue,
    refMode,
    refResetKey,
    isConfirmOpen,
    hasPendingChange,
    setSelected,
    setDropdownClose,
    setConfirmRequest,
    setConfirmChange,
    setCancelChange,
  }
}
